import React, { useMemo } from 'react';
import { T, Var, DateTime, useGT } from 'gt-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatCurrency } from '@erp/shared-utils';
import { RebatePoint, OverallRebateMetrics } from '@erp/shared-types';
import { getRebateAmount } from '@/utils/rebateAmountSelector';
import { LINE_TYPE_LABELS } from '@/types/rebateAnalytics';
import { formatRebateStatusMonthYearUtc } from '@/utils/rebateStatusCardDateFormat';
interface RebateStatusCardsProps {
  totalOwed: number;
  compact?: boolean; // For smaller cards if needed
  // Enhanced props for summary card style
  summaryStyle?: boolean;
  totalProjected: number;
  currentActualPosition?: RebatePoint | null;
  currentPredictedPosition?: RebatePoint | null;
  currentReportedPosition?: RebatePoint | null;
  // New props for conditional/non-conditional breakdown
  conditionalRebateYtd?: number;
  nonConditionalRebateYtd?: number;
  isCumulative?: boolean; // Whether to show cumulative (YTD) or month-by-month values
  tagTotals?: Map<string, number>; // Map of tag name -> total amount
  // Overall metrics for rebate percentage calculations
  overallMetrics?: {
    actual: OverallRebateMetrics;
    predicted: OverallRebateMetrics;
  };
  predictionAlgorithm?: string; // Which prediction strategy is used (e.g. 'linear-regression', 'conservative')
}

const RebateStatusCards: React.FC<RebateStatusCardsProps> = ({
  totalOwed,
  compact = false,
  summaryStyle = false,
  totalProjected,
  currentActualPosition,
  currentPredictedPosition,
  currentReportedPosition,
  conditionalRebateYtd,
  nonConditionalRebateYtd,
  isCumulative = true,
  tagTotals,
  overallMetrics,
  predictionAlgorithm,
}) => {
  const gt = useGT();
  const algorithmLabel = predictionAlgorithm
    ? (LINE_TYPE_LABELS[predictionAlgorithm] ?? predictionAlgorithm)
    : null;
  const monthText = currentActualPosition
    ? formatRebateStatusMonthYearUtc(currentActualPosition.date)
    : gt('Current Month', {
        context:
          'rebate status cards, month label when no actual position date',
      });

  const expectedRebateLabel = isCumulative ? (
    <T context="rebate status card metric label">Expected Rebate YTD</T>
  ) : (
    <T context="rebate status card metric label with month">
      Expected Rebate <Var>{monthText}</Var>
    </T>
  );
  const nonConditionalLabel = isCumulative ? (
    <T context="rebate status card metric label">
      Expected Non-Conditional Rebate YTD
    </T>
  ) : (
    <T context="rebate status card metric label with month">
      Expected Non-Conditional Rebate <Var>{monthText}</Var>
    </T>
  );
  const conditionalLabel = isCumulative ? (
    <T context="rebate status card metric label">
      Expected Conditional Rebate YTD
    </T>
  ) : (
    <T context="rebate status card metric label with month">
      Expected Conditional Rebate <Var>{monthText}</Var>
    </T>
  );

  const expectedRebateEndLabel = isCumulative ? (
    <T context="rebate status card metric label">Predicted Rebate Term End</T>
  ) : (
    <T context="rebate status card metric label with month">
      Predicted Rebate for <Var>{monthText}</Var> End
    </T>
  );

  const reportedRebateLabel = isCumulative ? (
    <T context="rebate status card metric label">Reported Rebate YTD</T>
  ) : (
    <T context="rebate status card metric label with month">
      Reported Rebate <Var>{monthText}</Var>
    </T>
  );

  // Status cards always show cumulative (YTD) values from backend's overallMetrics
  // The backend calculates these using invoice volumes (not n-counted breakdown volumes)
  // and includes the correct rebatePercentage calculation
  const displayMetrics = overallMetrics;

  // Calculate rebateable percentage (rebate / rebateableVolume)
  // rebateableVolume is the sum of line items with productGroupId != null
  const rebateablePercentages = useMemo(() => {
    if (!displayMetrics) return null;

    const actualRebateablePct =
      displayMetrics.actual.rebateableVolume > 0
        ? (displayMetrics.actual.totalRebateAmount /
            displayMetrics.actual.rebateableVolume) *
          100
        : 0;

    const predictedRebateablePct =
      displayMetrics.predicted.rebateableVolume > 0
        ? (displayMetrics.predicted.totalRebateAmount /
            displayMetrics.predicted.rebateableVolume) *
          100
        : 0;

    return {
      actual: actualRebateablePct,
      predicted: predictedRebateablePct,
    };
  }, [displayMetrics]);

  const rebatePctTitle = isCumulative ? (
    <T context="rebate status overall metrics title">Rebate % YTD</T>
  ) : (
    <T context="rebate status overall metrics title with month">
      Rebate % <Var>{monthText}</Var>
    </T>
  );
  const predictedRebatePctTitle = isCumulative ? (
    <T context="rebate status overall metrics title">
      Predicted Rebate % Term End
    </T>
  ) : (
    <T context="rebate status overall metrics title with month">
      Predicted Rebate % <Var>{monthText}</Var> End
    </T>
  );

  // If using summary style, render enhanced layout in a 2x2 grid
  if (summaryStyle) {
    // Convert tag totals map to array for rendering
    const tagCards = tagTotals
      ? Array.from(tagTotals.entries()).map(([tagName, amount]) => ({
          tagName,
          amount,
        }))
      : [];

    return (
      <div className="space-y-6">
        {/* Filtered Metrics Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs font-semibold text-muted-foreground px-2">
              <T context="rebate status section heading">Filtered Metrics</T>
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Expected Rebate */}
            <Card className="bg-green-50 border-green-200">
              <CardContent className="text-center p-4">
                <div className="text-xs font-bold text-muted-foreground mb-2">
                  {expectedRebateLabel}
                </div>
                <div className="text-lg font-bold text-black mb-1">
                  {formatCurrency(
                    currentActualPosition
                      ? getRebateAmount(currentActualPosition, isCumulative)
                      : 0,
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {currentActualPosition ? (
                    <DateTime
                      options={{
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        timeZone: 'UTC',
                      }}
                    >
                      {currentActualPosition.date}
                    </DateTime>
                  ) : (
                    <T context="rebate status card period label when no data">
                      Current Period
                    </T>
                  )}
                </div>
              </CardContent>
            </Card>
            {/* Expected Rebate Term End */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="text-center p-4">
                <div className="text-xs font-bold text-muted-foreground mb-2">
                  {expectedRebateEndLabel}
                </div>
                <div className="text-lg font-bold text-black mb-1">
                  {formatCurrency(
                    isCumulative
                      ? totalProjected
                      : currentPredictedPosition
                        ? getRebateAmount(
                            currentPredictedPosition,
                            isCumulative,
                          )
                        : 0,
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {algorithmLabel ? (
                    <Badge
                      variant="outline"
                      className="text-[10px] px-1.5 py-0 font-medium text-blue-600 border-blue-300"
                    >
                      {algorithmLabel}
                    </Badge>
                  ) : (
                    <T context="rebate status card period fallback">
                      End of Period
                    </T>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Reported Rebate YTD */}
            {currentReportedPosition ? (
              <Card className="bg-slate-100 border-slate-300">
                <CardContent className="text-center p-4">
                  <div className="text-xs font-bold text-muted-foreground mb-2">
                    {reportedRebateLabel}
                  </div>
                  <div className="text-lg font-bold text-black mb-1">
                    {formatCurrency(
                      getRebateAmount(currentReportedPosition, isCumulative),
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    <DateTime
                      options={{
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        timeZone: 'UTC',
                      }}
                    >
                      {currentReportedPosition.date}
                    </DateTime>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-slate-50 border-slate-200 opacity-50">
                <CardContent className="text-center p-4">
                  <div className="text-xs font-bold text-muted-foreground mb-2">
                    {reportedRebateLabel}
                  </div>
                  <div className="text-lg font-bold text-slate-500 mb-1">
                    <T context="rebate status not applicable abbreviation">
                      N/A
                    </T>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    <T context="rebate status no reported data">
                      No reported data
                    </T>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Expected Non-Conditional Rebate YTD - show if breakdown is available */}
            {conditionalRebateYtd !== undefined &&
            nonConditionalRebateYtd !== undefined ? (
              <Card className="bg-gray-50 border-purple-200">
                <CardContent className="text-center p-4">
                  <div className="text-xs font-bold text-muted-foreground mb-2">
                    {nonConditionalLabel}
                  </div>
                  <div className="text-lg font-bold text-black mb-1">
                    {formatCurrency(nonConditionalRebateYtd)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    <T context="rebate status non-conditional subtitle">
                      Volume & Manual Rules
                    </T>
                  </div>
                </CardContent>
              </Card>
            ) : (
              /* Fallback: show placeholder card when breakdown not available */
              <Card className="bg-gray-50 border-gray-200 opacity-50">
                <CardContent className="text-center p-4">
                  <div className="text-xs font-bold text-muted-foreground mb-2">
                    {nonConditionalLabel}
                  </div>
                  <div className="text-lg font-bold text-gray-500 mb-1">
                    <T context="rebate status not applicable abbreviation">
                      N/A
                    </T>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    <T context="rebate status rule breakdown unavailable">
                      Rule data needed
                    </T>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Expected Conditional Rebate YTD */}
            {conditionalRebateYtd !== undefined &&
            nonConditionalRebateYtd !== undefined ? (
              <Card className="bg-gray-50 border-gray-200">
                <CardContent className="text-center p-4">
                  <div className="text-xs font-bold text-muted-foreground mb-2">
                    {conditionalLabel}
                  </div>
                  <div className="text-lg font-bold text-gray-900 mb-1">
                    {formatCurrency(conditionalRebateYtd)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    <T context="rebate status conditional rebate subtitle">
                      Growth Rules Only
                    </T>
                  </div>
                </CardContent>
              </Card>
            ) : (
              /* Fallback: show placeholder card when breakdown not available */
              <Card className="bg-gray-50 border-gray-200 opacity-50">
                <CardContent className="text-center p-4">
                  <div className="text-xs font-bold text-muted-foreground mb-2">
                    {conditionalLabel}
                  </div>
                  <div className="text-lg font-bold text-gray-500 mb-1">
                    <T context="rebate status not applicable abbreviation">
                      N/A
                    </T>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    <T context="rebate status rule breakdown unavailable">
                      Rule data needed
                    </T>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tag-based cards - also affected by filters */}
            {tagCards.map(({ tagName, amount }) => {
              const tagLabel = isCumulative ? (
                <T context="rebate status tag card title cumulative">
                  <Var>{tagName}</Var> Rebate YTD
                </T>
              ) : (
                <T context="rebate status tag card title monthly">
                  <Var>{tagName}</Var> Rebate for <Var>{monthText}</Var>
                </T>
              );

              return (
                <Card key={tagName} className="bg-gray-50 border-gray-200">
                  <CardContent className="text-center p-4">
                    <div className="text-xs font-bold text-muted-foreground mb-2">
                      {tagLabel}
                    </div>
                    <div className="text-lg font-bold text-black mb-1">
                      {formatCurrency(amount)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      <T context="rebate status tag card subtitle">
                        Tagged Rules
                      </T>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Overall Rebate Percentage Cards - Not Affected by Filters */}
        {displayMetrics && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs font-semibold text-muted-foreground px-2">
                <T context="rebate status overall metrics section heading">
                  Overall Metrics (Not Affected by Filters)
                </T>
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>
            <div className="bg-muted/30 rounded-lg p-4 border-2 border-dashed border-muted-foreground/20">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* YTD Rebate Percentage */}
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="p-4">
                    <div className="text-xs font-bold text-muted-foreground mb-3 text-center">
                      {rebatePctTitle}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      {/* All Volume */}
                      <div className="text-center">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                          <T context="rebate status volume breakdown label">
                            All Volume
                          </T>
                        </div>
                        <div className="text-lg font-bold text-purple-700">
                          {displayMetrics.actual.rebatePercentage.toFixed(2)}%
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {formatCurrency(
                            displayMetrics.actual.totalRebateAmount,
                          )}{' '}
                          / {formatCurrency(displayMetrics.actual.totalVolume)}
                        </div>
                      </div>
                      {/* Rebateable Volume */}
                      <div className="text-center border-l border-purple-200 pl-4">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                          <T context="rebate status volume breakdown label">
                            Rebateable
                          </T>
                        </div>
                        <div className="text-lg font-bold text-purple-900">
                          {rebateablePercentages?.actual.toFixed(2) ?? '0.00'}%
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {formatCurrency(
                            displayMetrics.actual.totalRebateAmount,
                          )}{' '}
                          /{' '}
                          {formatCurrency(
                            displayMetrics.actual.rebateableVolume,
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Predicted Rebate Percentage */}
                <Card className="bg-indigo-50 border-indigo-200">
                  <CardContent className="p-4">
                    <div className="text-xs font-bold text-muted-foreground mb-1 text-center">
                      {predictedRebatePctTitle}
                    </div>
                    {algorithmLabel && (
                      <div className="mb-2 text-center">
                        <Badge
                          variant="outline"
                          className="text-[10px] px-1.5 py-0 font-medium text-indigo-600 border-indigo-300"
                        >
                          {algorithmLabel}
                        </Badge>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                      {/* All Volume */}
                      <div className="text-center">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                          <T context="rebate status volume breakdown label">
                            All Volume
                          </T>
                        </div>
                        <div className="text-lg font-bold text-indigo-700">
                          {displayMetrics.predicted.rebatePercentage.toFixed(2)}
                          %
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {formatCurrency(
                            displayMetrics.predicted.totalRebateAmount,
                          )}{' '}
                          /{' '}
                          {formatCurrency(displayMetrics.predicted.totalVolume)}
                        </div>
                      </div>
                      {/* Rebateable Volume */}
                      <div className="text-center border-l border-indigo-200 pl-4">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                          <T context="rebate status volume breakdown label">
                            Rebateable
                          </T>
                        </div>
                        <div className="text-lg font-bold text-indigo-900">
                          {rebateablePercentages?.predicted.toFixed(2) ??
                            '0.00'}
                          %
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {formatCurrency(
                            displayMetrics.predicted.totalRebateAmount,
                          )}{' '}
                          /{' '}
                          {formatCurrency(
                            displayMetrics.predicted.rebateableVolume,
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Original style
  const cardPadding = compact ? 'p-4' : 'p-6';
  const titleSize = compact ? 'text-lg' : 'text-2xl';
  const subtitleSize = compact ? 'text-sm' : 'text-lg';

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Total Owed */}
      <Card className={compact ? '' : 'bg-purple-600 border-purple-700'}>
        <CardContent className={`text-center ${cardPadding}`}>
          <div
            className={`${subtitleSize} font-medium mb-2 ${
              compact ? 'text-green-600' : 'text-white'
            }`}
          >
            {expectedRebateLabel}
          </div>
          <div
            className={`${titleSize} font-bold ${
              compact ? 'text-blue-600' : 'text-white'
            }`}
          >
            {formatCurrency(totalOwed)}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RebateStatusCards;
